import * as React from 'react'

import clsx from 'clsx'
import type { EntityWithIndices } from 'twitter-text'

import { LinkifiedText } from '@buffer-mono/legacy-bufferapp-components'
import { CloseIcon } from '@buffer-mono/popcorn'

import { useIsIntegrationUploading } from '~publish/legacy/integrations-bar/state/useIsIntegrationUploading'

import { parseThreadsLinks } from '../../../../utils/channels/threads/helpers'
import { parseTwitterLinks } from '../../../../utils/channels/twitter/helpers'
import ComposerActionCreators from '../../action-creators/ComposerActionCreators'
import type Draft from '../../entities/Draft/Draft'
import type { Post } from '../../entities/Draft/Draft'
import ComposerStore from '../../stores/ComposerStore'
import { AttachmentSwitch } from '../attachment-switch/AttachmentSwitch'
import { ComposerIntegrationsBar } from '../ComposerIntegrationsBar'
import styles from '../css/CommonThread.module.css'
import { EditorWrapper } from '../EditorWrapper'
import MediaAttachment from '../MediaAttachment'
import type { ThreadComponentProps } from './types'
import LinkAttachment from '../LinkAttachment'
import EditorNotices from '../notices/EditorNotices'
import RetweetAttachment from '../RetweetAttachment'
import SuggestedMediaBox from '../SuggestedMediaBox'

import threadStyles from './ThreadComponents.module.css'

const getBorderClassNames = (index: number, activeThreadId: number): string => {
  return clsx(
    index === 0 ? styles.noTopBorder : styles.withTopBorder,
    index === activeThreadId - 1
      ? styles.withBottomBorder
      : styles.noBottomBorder,
  )
}

interface CommonThreadProps extends ThreadComponentProps {
  renderWrapper: (children: React.ReactNode) => React.ReactElement
  renderThreadItem?: (
    children: React.ReactNode,
    index: number,
  ) => React.ReactElement
}

export const CommonThread = ({
  draft,
  editorClassName,
  editorContainerClassName,
  suggestedMediaBoxClassName,
  shouldAutoFocus,
  uploader,
  renderWrapper,
  renderThreadItem,
  composerRootRef,
}: CommonThreadProps): JSX.Element => {
  const { isIntegrationUploading } = useIsIntegrationUploading()

  const getEditor = (
    draftEntity: Draft,
    key: string | number,
    hasThread: boolean,
  ) => (
    <React.Fragment key={key}>
      <EditorNotices draft={draft} />
      <div className={editorContainerClassName}>
        <div className={editorClassName}>
          <EditorWrapper
            draft={draftEntity}
            shouldAutoFocus={shouldAutoFocus}
            attachmentGlanceHasNoThumbnail
            composerRootRef={composerRootRef}
          />
          <MediaAttachment draft={draftEntity} uploader={uploader} />
        </div>
        <LinkAttachment draft={draft} />
        <RetweetAttachment draft={draft} />
        <AttachmentSwitch draft={draft} />
        <SuggestedMediaBox
          draft={draft}
          className={suggestedMediaBoxClassName}
        />
        <ComposerIntegrationsBar
          draft={draftEntity}
          composerRootRef={composerRootRef}
          uploader={uploader}
        />
        {hasThread && (
          <button
            type="button"
            data-testid="remove-threaded-draft-button"
            className={threadStyles.removeTweet}
            onClick={(e: React.MouseEvent): void => {
              e.preventDefault()
              e.stopPropagation()
              ComposerActionCreators.deleteThreadedDraft(draft.id)
            }}
            aria-label={`Remove ${draft.service.nameOfPost}`}
          >
            <CloseIcon />
          </button>
        )}
      </div>
    </React.Fragment>
  )

  const changeEditor = (threadId: number) => {
    if (isIntegrationUploading) {
      return
    }

    ComposerActionCreators.switchActiveThreadEditor(draft.id, threadId)
  }

  if (!draft.hasThread()) {
    return getEditor(draft, draft.id, false)
  }

  const activeThreadId = ComposerStore.getActiveThreadId()

  const parseLinks = (text: string): (EntityWithIndices | null)[] => {
    if (draft.service?.isTwitter()) {
      return parseTwitterLinks(text)
    } else if (draft.service?.isThreads()) {
      return parseThreadsLinks(text)
    }
    return []
  }

  const renderUnfocusedEditor = (
    threadedDraft: Post,
    i: number,
  ): JSX.Element => {
    const hasMedia =
      (Array.isArray(threadedDraft.images) &&
        threadedDraft.images.length > 0) ||
      !!threadedDraft.video ||
      !!threadedDraft.gif

    const hasLink = !!threadedDraft.link
    const hasRetweet = !!threadedDraft.retweet
    const isEmpty =
      threadedDraft.text.trim() === '' &&
      !hasRetweet &&
      (!threadedDraft.images || threadedDraft.images.length === 0) &&
      !threadedDraft.gif &&
      !threadedDraft.video

    return (
      <div
        onClick={(): void => changeEditor(i)}
        className={clsx(
          threadStyles.unfocusedEditor,
          getBorderClassNames(i, activeThreadId),
        )}
        data-testid={`threads-unfocused-editor-${i}`}
      >
        {isEmpty && (
          <div
            className={clsx(
              threadStyles.unfocusedEditorText,
              threadStyles.unfocusedEditorTextNoMargin,
            )}
          >
            Add a {draft.service.nameOfPost?.toLowerCase()}…
          </div>
        )}
        {threadedDraft.text && (
          <div
            className={clsx(
              threadStyles.unfocusedEditorText,
              hasMedia || hasRetweet || hasLink
                ? threadStyles.unfocusedEditorTextWithMargin
                : threadStyles.unfocusedEditorTextNoMargin,
            )}
          >
            {threadedDraft.text && (
              <LinkifiedText
                links={parseLinks(threadedDraft.text)}
                size="mini"
                whitespace="pre-wrap"
                newTab
                unstyled
              >
                {threadedDraft.text}
              </LinkifiedText>
            )}
          </div>
        )}
        {hasMedia && (
          <div className={threadStyles.unfocusedEditorMediaContainer}>
            {threadedDraft.images &&
              threadedDraft.images.map((image: any) => (
                <img
                  src={image.url}
                  key={image.url}
                  className={threadStyles.unfocusedEditorMedia}
                  alt="Thread image"
                />
              ))}
            {threadedDraft.gif && (
              <img
                src={threadedDraft.gif.url}
                className={threadStyles.unfocusedEditorMedia}
                alt="Thread GIF"
              />
            )}
            {threadedDraft.video && (
              <img
                src={threadedDraft.video.thumbnail}
                className={threadStyles.unfocusedEditorMedia}
                alt="Thread video thumbnail"
              />
            )}
          </div>
        )}

        {hasLink && (
          <div className={threadStyles.styledLinkAttachment}>
            <LinkAttachment draft={threadedDraft as Draft} disabled />
          </div>
        )}

        {hasRetweet && (
          <div className={threadStyles.styledRetweetAttachment}>
            <RetweetAttachment draft={threadedDraft} disabled />
          </div>
        )}

        <div className={threadStyles.unfocusedEditorOverlay} />
      </div>
    )
  }

  const threadItems = draft.thread.map((threadedDraft: Post, i: number) => {
    if (i === activeThreadId) {
      const editorElement = getEditor(draft, activeThreadId, true)

      return renderThreadItem
        ? renderThreadItem(editorElement, i)
        : editorElement
    }

    const unfocusedEditor = renderUnfocusedEditor(threadedDraft, i)

    return renderThreadItem
      ? renderThreadItem(unfocusedEditor, i)
      : unfocusedEditor
  })

  return renderWrapper(threadItems)
}
